import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { FEATURES } from '@/pages/funciones/featuresData';
import { useTranslation } from 'react-i18next';
import {
  Play,
  Plane,
  Users,
  Wallet,
  Map as MapIcon,
  FolderOpen,
  CheckCircle2,
  Camera,
  Calendar,
  Plus,
  Star,
  Smartphone,
  Heart,
  MapPin,
  Bell,
  Receipt,
  Globe,
  LogIn,
  ArrowRight,
  Mail,
  Zap,
  PenLine,
  Wand2,
  Home,
  MoreHorizontal,
  Cloud,
  Activity,
  Sparkles,
  Bed,
  FileText,
  Check,
  Mic,
  Vote,
  BookOpen,
  Compass,
  ChevronLeft,
  ChevronRight,
  Headphones,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GetTheAppButton, isIOSDevice } from '@/components/GooglePlayBadge';
import { isInstalledApp } from '@/utils/installedApp';
import { RoutesSpotlight } from '@/components/landing/RoutesSpotlight';
import { WhatsNew } from '@/components/landing/WhatsNew';

/* --------------------------------------------------------------------------
 * Marketing landing page — Stripe / Splitwise-style
 * Sections: Header · Hero (with phone mockup) · 4 alternating feature rows
 *           · Pricing teaser · Install · Final CTA · Footer
 * ------------------------------------------------------------------------ */

// Hero dream backgrounds — one is picked at random per visit. UtmTracker
// already logs visits, so variant performance can be analyzed later.
const HERO_VARIANTS = [
  { id: 'coast', src: '/hero-dream.webp', srcAvif: '/hero-dream.avif', mobile: '/hero-dream-mobile.webp', mobileAvif: '/hero-dream-mobile.avif' },
  { id: 'fjord', src: '/hero-dream-fjord.webp', srcAvif: '/hero-dream-fjord.avif', mobile: '/hero-dream-fjord-mobile.webp', mobileAvif: '/hero-dream-fjord-mobile.avif' },
  { id: 'desert', src: '/hero-dream-desert.webp', srcAvif: '/hero-dream-desert.avif', mobile: '/hero-dream-desert-mobile.webp', mobileAvif: '/hero-dream-desert-mobile.avif' },
  { id: 'tropical', src: '/hero-dream-tropical.webp', srcAvif: '/hero-dream-tropical.avif', mobile: '/hero-dream-tropical-mobile.webp', mobileAvif: '/hero-dream-tropical-mobile.avif' },
];
// Module-level pick: chosen by the inline preload script in index.html
// (window.__HERO_PICK) so the image React renders is EXACTLY the one already
// downloading with high priority since HTML parse time (LCP win). Falls back
// to a local random pick if the script didn't run.
const HERO_PICK =
  HERO_VARIANTS.find((v) => v.id === window.__HERO_PICK) ||
  HERO_VARIANTS[Math.floor(Math.random() * HERO_VARIANTS.length)];

const GROUP_IMAGE = '/landing/feature-grupo.webp';
const CAFE_IMAGE = '/landing/feature-cafe.webp';
const DOCS_IMAGE = '/landing/feature-docs.webp';

/* ----------------------------- Header ----------------------------------- */
const Header = () => {
  const { t } = useTranslation('landing');
  return (
  <header
    data-testid="landing-header"
    className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200"
  >
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-3">
      <Link to="/" data-testid="landing-logo" className="flex items-center shrink-0">
        <img
          src="/tovias-logo-header-sm.webp"
          srcSet="/tovias-logo-header-sm.webp 178w, /tovias-logo-header.webp 225w"
          sizes="(max-width: 640px) 144px, 176px"
          alt="TOVÍAS"
          width="178"
          height="70"
          fetchPriority="high"
          className="h-8 sm:h-11 w-auto"
        />
      </Link>
      <div className="flex items-center gap-1 sm:gap-3 min-w-0">
        <Link
          to="/rutas"
          data-testid="nav-routes-link"
          className="text-xs sm:text-sm font-semibold text-stone-700 hover:text-teal-800 px-2 sm:px-3 py-2 whitespace-nowrap inline-flex items-center gap-1.5 rounded-full hover:bg-teal-50"
        >
          <Compass className="w-4 h-4 text-teal-600" aria-hidden="true" />
          <span>Rutas</span>
          <span className="hidden md:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-teal-700 bg-teal-50 ring-1 ring-teal-200 rounded-full px-1.5 py-0.5">
            <Headphones className="w-3 h-3" /> audioguía
          </span>
        </Link>
        <Link
          to="/email-login"
          data-testid="nav-login-link"
          aria-label={t('nav.login')}
          className="text-xs sm:text-sm font-semibold text-teal-700 hover:text-teal-800 px-2 sm:px-3 py-2 whitespace-nowrap inline-flex items-center gap-1.5 rounded-full hover:bg-teal-50"
        >
          <LogIn className="w-4 h-4 sm:hidden" aria-hidden="true" />
          <span>{t('nav.login')}</span>
        </Link>
        <Link to="/register" data-testid="nav-register-btn" className="flex-shrink-0">
          <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-3 sm:px-5 py-2 text-xs sm:text-base font-semibold shadow-sm whitespace-nowrap">
            {/* Very small viewports (Samsung 320-360px w/ accessibility zoom)
                can't fit "Empieza gratis" alongside logo + "Entrar" — we
                'd overflow to the right and clip the hero. Shorter label
                keeps the header inside the viewport on ~320px screens. */}
            <span className="sm:hidden">Empezar</span>
            <span className="hidden sm:inline">{t('hero.ctaPrimary')}</span>
          </Button>
        </Link>
      </div>
    </div>
  </header>
  );
};

/* ============================================================
 * useInView — simple IntersectionObserver hook
 * ============================================================ */
const useInView = (options = { threshold: 0.18, once: true }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (options.once) obs.disconnect();
        } else if (!options.once) {
          setInView(false);
        }
      },
      { threshold: options.threshold || 0.18 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [options.threshold, options.once]);
  return [ref, inView];
};

/* ============================================================
 * Polaroid Gallery — "Vive viajes así"
 * 5 real travel photos in rotated polaroid frames with washi tape
 * ============================================================ */
const POLAROIDS = [
  // Mix of real Tovías UI screenshots + memory photos — shows BOTH what
  // the app looks like AND the kind of memories it helps you keep.
  { src: '/landing/ui/polaroid-tokio.webp',   caption: 'Tu próxima ruta',           rotate: '-5deg', tape: 'top-left',   accent: '#14b8a6', kind: 'ui' },
  { src: '/landing/helado.webp',              caption: 'Sabores de domingo',        rotate: '4deg',  tape: 'top-right',  accent: '#fbbf24', kind: 'photo' },
  { src: '/landing/ui/polaroid-catalog.webp', caption: 'Inspírate con la comunidad', rotate: '-3deg', tape: 'top-center', accent: '#60a5fa', kind: 'ui' },
  { src: '/landing/atardecer.webp',           caption: 'El cielo de aquel día',     rotate: '6deg',  tape: 'top-left',   accent: '#fb7185', kind: 'photo' },
  { src: '/landing/ui/polaroid-costa.webp',   caption: 'Comparte tu itinerario',    rotate: '-7deg', tape: 'top-right',  accent: '#34d399', kind: 'ui' },
];

const tapeStyles = {
  'top-left':  { top: '-12px', left: '14px',  rotate: '-12deg' },
  'top-right': { top: '-10px', right: '18px', rotate: '14deg' },
  'top-center':{ top: '-14px', left: '50%',   transform: 'translateX(-50%) rotate(-3deg)' },
};

const Polaroid = ({ photo, index, inView }) => {
  const tape = tapeStyles[photo.tape] || tapeStyles['top-left'];
  return (
    <figure
      data-testid={`polaroid-${index}`}
      className="relative bg-white pt-3 pb-6 px-3 shadow-[0_20px_50px_-18px_rgba(15,23,42,0.35)] hover:shadow-[0_28px_70px_-15px_rgba(15,23,42,0.45)] transition-all duration-500 hover:-translate-y-2 hover:!rotate-0 cursor-default"
      style={{
        transform: inView ? `rotate(${photo.rotate})` : `translateY(40px) rotate(${photo.rotate})`,
        opacity: inView ? 1 : 0,
        transitionProperty: 'transform, opacity, box-shadow',
        transitionDuration: '700ms',
        transitionDelay: `${index * 100}ms`,
      }}
    >
      {/* Washi tape */}
      <span
        aria-hidden
        className="absolute w-20 h-5 opacity-80 shadow-sm"
        style={{
          background: `repeating-linear-gradient(135deg, ${photo.accent}cc 0 8px, ${photo.accent}99 8px 16px)`,
          ...tape,
        }}
      />
      <div className={`w-full aspect-square overflow-hidden ${photo.kind === 'ui' ? 'bg-white ring-1 ring-stone-200/60' : 'bg-stone-100'}`}>
        <img
          src={photo.src}
          alt={photo.caption}
          loading="lazy"
          decoding="async"
          className={`w-full h-full ${photo.kind === 'ui' ? 'object-cover object-top' : 'object-cover'}`}
        />
      </div>
      <figcaption
        className="mt-3 px-1 text-stone-700 text-center leading-tight"
        style={{ fontFamily: '"Caveat", "Bradley Hand", cursive', fontSize: '1.05rem', fontWeight: 500 }}
      >
        {photo.caption}
      </figcaption>
    </figure>
  );
};

const PolaroidGallery = () => {
  const [ref, inView] = useInView({ threshold: 0.15, once: true });
  return (
    <section
      ref={ref}
      data-testid="polaroid-gallery"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{
        background:
          'radial-gradient(circle at 20% 10%, #fff7ed 0%, transparent 45%), radial-gradient(circle at 80% 80%, #ecfeff 0%, transparent 50%), #fafaf9',
      }}
    >
      {/* Subtle grain */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-multiply"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' /%3E%3C/svg%3E")',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 lg:mb-20">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold mb-5">
            <Heart className="w-3.5 h-3.5" /> Para guardar lo importante
          </span>
          <h2
            className="text-stone-900 leading-[1.05] mb-4"
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 700,
              fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
            }}
          >
            Vive viajes <em className="text-teal-700 not-italic">así</em>.
          </h2>
          <p className="text-stone-600 max-w-xl mx-auto leading-relaxed">
            Tovías no es solo logística. Es el lugar donde tus mejores momentos
            <br className="hidden sm:inline" /> se quedan para siempre.
          </p>
        </div>

        {/* Polaroid grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8 lg:gap-4 max-w-5xl mx-auto">
          {POLAROIDS.map((p, i) => (
            <Polaroid key={p.src} photo={p} index={i} inView={inView} />
          ))}
        </div>

        <p
          className="text-center mt-14 text-stone-500"
          style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}
        >
          — los recuerdos que importan, en un mismo lugar —
        </p>
      </div>
    </section>
  );
};

/* ============================================================
 * App Showcase — 3 scroll-animated phone screens
 * ============================================================ */
const PhoneScreen = ({ children, label, screenshot, screenshotAlt }) => (
  <div className="relative mx-auto w-[190px] sm:w-[210px] aspect-[9/19] rounded-[2rem] bg-stone-900 p-2 shadow-[0_24px_50px_-22px_rgba(15,23,42,0.35)] ring-1 ring-stone-900/10">
    <div className="absolute top-2 left-1/2 -translate-x-1/2 h-4 w-16 rounded-full bg-stone-900 z-10" />
    <div className="relative h-full w-full rounded-[1.6rem] bg-gradient-to-b from-stone-50 to-white overflow-hidden">
      {screenshot ? (
        // feb 28 msg 823 · Reyes · Real app screenshot captured at
        // 390×844 (iPhone 14 viewport) so the aspect matches the phone
        // frame perfectly on Android & iOS mockups without breaking.
        <img
          src={screenshot}
          alt={screenshotAlt || label}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-top select-none"
          draggable="false"
        />
      ) : (
        <>
          <div className="flex items-center justify-between px-4 pt-2.5 text-[9px] font-semibold text-stone-700">
            <span>9:41</span>
            <span className="opacity-60">●●●●</span>
          </div>
          {children}
        </>
      )}
    </div>
    <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-stone-500">
      {label}
    </span>
  </div>
);

const ScreenReservations = () => (
  <div className="px-2.5 mt-3">
    <p className="text-[9px] uppercase tracking-wider text-teal-700 font-bold">Reservas</p>
    <h4 className="text-stone-900 text-sm font-bold mt-0.5">Mallorca · Junio</h4>
    <div className="mt-2 space-y-1.5">
      {[
        { Icon: Plane, color: '#0ea5e9', name: 'MAD → PMI', sub: '07:30 · IB3704', state: 'OK' },
        { Icon: MapPin, color: '#f97316', name: 'Hotel C. Vermell', sub: 'Check-in 16h', state: 'OK' },
        { Icon: Receipt, color: '#a855f7', name: 'Coche Sixt', sub: '4 días', state: '...' },
      ].map((r, i) => {
        const Icon = r.Icon;
        return (
          <div key={i} className="flex items-center gap-1.5 p-1.5 rounded-lg bg-white border border-stone-200">
            <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${r.color}1f` }}>
              <Icon className="w-3 h-3" style={{ color: r.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-stone-900 truncate">{r.name}</p>
              <p className="text-[8px] text-stone-500 truncate">{r.sub}</p>
            </div>
            <span className={`text-[7px] font-bold px-1 py-0.5 rounded-full ${r.state === 'OK' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
              {r.state === 'OK' ? '✓' : '…'}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const ScreenExpenses = () => (
  <div className="px-2.5 mt-3">
    <p className="text-[9px] uppercase tracking-wider text-teal-700 font-bold">Gastos</p>
    <h4 className="text-stone-900 text-sm font-bold mt-0.5">450,50 €</h4>
    <p className="text-[9px] text-stone-500">4 gastos · 4 personas</p>
    <div className="mt-2 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 text-white p-2">
      <p className="text-[8px] uppercase font-bold opacity-80">Tu balance</p>
      <p className="text-sm font-bold mt-0.5">+ 45,20 €</p>
      <div className="h-1 w-full bg-white/25 rounded-full mt-1.5 overflow-hidden">
        <div className="h-full w-[68%] bg-white rounded-full" />
      </div>
    </div>
    <div className="mt-2 space-y-1">
      {[
        { name: 'Cena viernes', who: 'María', amount: '85,40' },
        { name: 'Coche', who: 'Carlos', amount: '120,00' },
        { name: 'Brunch', who: 'Lucía', amount: '35,10' },
      ].map((e, i) => (
        <div key={i} className="flex items-center justify-between text-[9px] py-0.5 border-b border-stone-100">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-stone-800 leading-tight truncate">{e.name}</p>
            <p className="text-teal-700 text-[8px]">Pagó {e.who}</p>
          </div>
          <span className="font-bold text-stone-900 tabular-nums shrink-0 ml-1">{e.amount}€</span>
        </div>
      ))}
    </div>
  </div>
);

const ScreenItinerary = () => (
  <div className="px-2.5 mt-3">
    <p className="text-[9px] uppercase tracking-wider text-teal-700 font-bold">Itinerario</p>
    <h4 className="text-stone-900 text-sm font-bold mt-0.5">Día 1 · Jueves</h4>
    <p className="text-[9px] text-stone-500 mb-2">13 de junio</p>
    {[
      { hour: '10:00', label: 'Vuelo MAD → PMI', Icon: Plane, color: '#0ea5e9' },
      { hour: '13:00', label: 'Check-in hotel', Icon: MapPin, color: '#f97316' },
      { hour: '17:00', label: 'Cala Mondragó', Icon: Camera, color: '#10b981' },
      { hour: '20:30', label: 'Sunset S. Foradada', Icon: Bell, color: '#f59e0b' },
    ].map((ev, i) => {
      const Icon = ev.Icon;
      return (
        <div key={i} className="flex items-start gap-1.5 mb-1.5 last:mb-0">
          <div className="flex flex-col items-center pt-0.5 shrink-0">
            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `${ev.color}1f` }}>
              <Icon className="w-2.5 h-2.5" style={{ color: ev.color }} />
            </div>
            {i < 3 && <div className="w-0.5 h-3 mt-0.5" style={{ background: '#e7e5e4' }} />}
          </div>
          <div className="flex-1 min-w-0 pt-0">
            <p className="text-[8px] uppercase tracking-wider text-stone-400 font-bold leading-none">{ev.hour}</p>
            <p className="text-[10px] font-semibold text-stone-800 mt-0.5 leading-tight truncate">{ev.label}</p>
          </div>
        </div>
      );
    })}
  </div>
);

const AppShowcase = () => {
  const [ref, inView] = useInView({ threshold: 0.2, once: true });
  const stats = usePublicStats();
  // sep 2026 fix ("promete 15+ rutas, el catálogo tiene 11") · antes era
  // un "15+" fijo en el código. Ahora es el conteo real de rutas
  // aprobadas en el catálogo (mismo filtro que /rutas), así que la home
  // nunca puede volver a prometer más de lo que hay.
  const routesCount = stats?.catalog_routes ?? null;
  const screens = [
    { Comp: ScreenReservations, label: 'Reservas' },
    { Comp: ScreenExpenses,     label: 'Gastos' },
    { Comp: ScreenItinerary,    label: 'Itinerario' },
  ];
  return (
    <section
      ref={ref}
      data-testid="app-showcase"
      className="relative py-16 lg:py-24 bg-white overflow-hidden"
    >
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <h2
            className="text-stone-900 leading-[1.1] mb-3"
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 700,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            }}
          >
            Todo el viaje, <span className="text-teal-700">en una sola app</span>.
          </h2>
          <p className="text-sm sm:text-base text-stone-600 leading-relaxed">
            Lugares, reservas, gastos e itinerario. Sin saltar entre apps, sin perder
            confirmaciones, sin discutir quién pagó qué.
          </p>
        </div>

        {/* Trío de móviles limpio: lugar (hero) + reservas + gastos, sin halo ni fondos de color */}
        <div
          className="relative max-w-3xl mx-auto flex justify-center items-end gap-6 sm:gap-10 min-h-[480px] sm:min-h-[560px] pb-12"
          data-testid="showcase-phone-hero"
        >
          <div
            aria-hidden="true"
            className="hidden md:block"
            style={{
              transform: inView ? 'translateY(0) scale(0.82)' : 'translateY(32px) scale(0.82)',
              opacity: inView ? 1 : 0,
              transition: 'transform 900ms cubic-bezier(0.16,1,0.3,1) 150ms, opacity 900ms ease 150ms',
              filter: 'drop-shadow(0 30px 40px rgba(15, 23, 42, 0.18))',
            }}
          >
            <PhoneScreen label="Reservas" screenshot="/screenshots/app-reservations.webp" screenshotAlt="Pantalla real de Reservas en Tovías" />
          </div>

          <div
            className="relative z-10"
            style={{
              transform: inView ? 'translateY(0) scale(1.12)' : 'translateY(40px) scale(1.12)',
              opacity: inView ? 1 : 0,
              transition: 'transform 900ms cubic-bezier(0.16,1,0.3,1), opacity 700ms ease',
              filter: 'drop-shadow(0 40px 60px rgba(15, 23, 42, 0.28))',
              animation: inView ? 'showcase-phone-float 6s ease-in-out infinite 1s' : 'none',
            }}
          >
            <PhoneScreen label="Lugares" screenshot="/screenshots/app-place.webp" screenshotAlt="Ficha de lugar en Tovías con foto a pantalla completa, audioguía y mapa" />
          </div>

          <div
            aria-hidden="true"
            className="hidden md:block"
            style={{
              transform: inView ? 'translateY(0) scale(0.82)' : 'translateY(32px) scale(0.82)',
              opacity: inView ? 1 : 0,
              transition: 'transform 900ms cubic-bezier(0.16,1,0.3,1) 300ms, opacity 900ms ease 300ms',
              filter: 'drop-shadow(0 30px 40px rgba(15, 23, 42, 0.18))',
            }}
          >
            <PhoneScreen label="Gastos" screenshot="/screenshots/app-expenses.webp" screenshotAlt="Pantalla real de Gastos en Tovías" />
          </div>

          <style>{`
            @keyframes showcase-phone-float {
              0%, 100% { transform: translateY(0) scale(1.12); }
              50%      { transform: translateY(-8px) scale(1.12); }
            }
          `}</style>
        </div>

        {/* feb 28 msg 811 · Reyes · CTA de instalación duplicado ARRIBA.
            Antes solo aparecía al final del landing (línea ~1355) —
            demasiado invisible. Aquí lo enseñamos INMEDIATAMENTE tras
            el hero + fila de móviles, con un banner atractivo y sin
            fricción (no pide registro para instalar el PWA). */}
        {!isInstalledApp() && (
        <div
          data-testid="showcase-install-banner"
          className="relative mt-12 max-w-3xl mx-auto rounded-3xl overflow-hidden ring-1 ring-teal-900/10 shadow-xl shadow-teal-900/10"
          style={{
            background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 55%, #99f6e4 100%)',
          }}
        >
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'radial-gradient(circle at 20% 40%, rgba(255,255,255,0.6), transparent 50%)',
          }} />
          <div className="relative px-6 py-7 sm:px-10 sm:py-9 flex flex-col sm:flex-row items-center gap-5 sm:gap-7">
            <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/20 backdrop-blur ring-1 ring-white/30 flex items-center justify-center">
              <Smartphone className="w-7 h-7 sm:w-8 sm:h-8 text-white" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <p className="text-[11px] uppercase tracking-wider font-bold text-teal-100 mb-1">
                Android · iPhone · Ordenador
              </p>
              <h3
                className="text-white text-xl sm:text-2xl leading-tight mb-1"
                style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 }}
              >
                Llévate Tovías en el móvil.
              </h3>
              <p className="text-sm text-teal-50 leading-snug">
                {isIOSDevice() ? 'Instálala en tu pantalla de inicio en 30 segundos. Muy pronto también en App Store.' : 'Descárgala en Google Play o instálala desde el navegador en iPhone y ordenador.'}
              </p>
            </div>
            <div className="shrink-0" data-testid="showcase-install-cta">
              <GetTheAppButton className="h-14" />
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

/* ----------------------- Phone mockup (CSS only) ------------------------ */
// Mirrors the REAL trip dashboard UI (hero cover + quick stats + bottom nav)
// so the hero phone shows exactly what users get inside the app.
const PhoneMockup = () => (
  <div
    className="relative mx-auto w-[280px] sm:w-[320px] lg:w-[340px] aspect-[9/19] rounded-[3rem] bg-stone-900 p-3 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)] ring-1 ring-stone-900/10"
    data-testid="hero-phone-mockup"
  >
    {/* Notch */}
    <div className="absolute top-3 left-1/2 -translate-x-1/2 h-6 w-28 rounded-full bg-stone-900 z-10" />
    {/* Screen */}
    <div className="relative h-full w-full rounded-[2.4rem] bg-stone-50 overflow-hidden flex flex-col">
      {/* ── Trip hero cover (like the real dashboard) ── */}
      <div className="relative h-[46%] shrink-0 overflow-hidden">
        <img
          src="/hero-dream-fjord-mobile.webp"
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/55 via-stone-950/15 to-stone-950/60" />
        {/* Status bar */}
        <div className="relative flex items-center justify-between px-5 pt-3.5 text-[10px] font-semibold text-white/95">
          <span>9:41</span>
          <span className="opacity-70">●●●●</span>
        </div>
        {/* Top action row */}
        <div className="relative flex items-center justify-between px-4 mt-2">
          <div className="w-8 h-8 rounded-xl bg-stone-900/45 backdrop-blur-sm flex items-center justify-center">
            <Home className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="flex gap-1.5">
            {[Camera, Globe, MoreHorizontal].map((Icon, i) => (
              <div key={i} className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-white" />
              </div>
            ))}
          </div>
        </div>
        {/* Chips */}
        <div className="relative flex gap-1.5 px-4 mt-2.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-900/45 backdrop-blur-sm text-white text-[8px] font-bold">
            ● Naturaleza
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-500/60 backdrop-blur-sm text-white text-[8px] font-bold">
            <Check className="w-2.5 h-2.5" /> Disponible sin conexión
          </span>
        </div>
        {/* Title + route */}
        <div className="relative px-4 mt-1.5">
          <h3
            className="text-white leading-tight drop-shadow"
            style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.35rem', fontWeight: 700 }}
          >
            Fiordos noruegos
          </h3>
          <p className="text-[9px] text-white/90 mt-0.5 flex items-center gap-1">
            <MapPin className="w-2.5 h-2.5 shrink-0" /> Bergen → Flåm → Geiranger
          </p>
          <p className="text-[9px] text-white/75 mt-0.5">8 – 14 jul · 4 viajeros</p>
          <div className="flex -space-x-1.5 mt-1.5">
            {['#fbbf24', '#fb7185', '#34d399', '#60a5fa'].map((c, i) => (
              <div key={i} className="w-5 h-5 rounded-full border-2 border-white/90 shadow-sm" style={{ background: c }} />
            ))}
          </div>
        </div>
        {/* Ask Tovías pill + locate FAB */}
        <div className="relative flex items-center justify-between px-4 mt-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-[10px] font-bold shadow-lg">
            <MapPin className="w-3 h-3 text-orange-300" /> Pregunta a Tovías
          </span>
          <div className="w-9 h-9 rounded-full bg-teal-600/90 ring-2 ring-white/70 flex items-center justify-center shadow-lg">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
              <circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            </svg>
          </div>
        </div>
      </div>

      {/* ── Quick stats grid (TIEMPO / PRESUPUESTO / ESTADO / PLAN) ── */}
      <div className="grid grid-cols-2 gap-2 px-3 mt-3">
        {[
          { Icon: Cloud, iconBg: 'bg-sky-50', iconColor: 'text-sky-500', label: 'Tiempo', value: '18,5 °C' },
          { Icon: Wallet, iconBg: 'bg-teal-50', iconColor: 'text-teal-600', label: 'Presupuesto', value: '1.194,83 €' },
          { Icon: Activity, iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', label: 'Estado', value: 'En curso' },
          { Icon: Sparkles, iconBg: 'bg-rose-50', iconColor: 'text-rose-500', label: 'Próximo plan', value: 'Ruta en barco' },
        ].map(({ Icon, iconBg, iconColor, label, value }) => (
          <div key={label} className="flex items-center gap-2 rounded-2xl bg-white border border-stone-200/80 px-2.5 py-2">
            <div className={`w-7 h-7 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
              <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
            </div>
            <div className="min-w-0">
              <p className="text-[7px] uppercase tracking-wider text-stone-400 font-bold">{label}</p>
              <p className="text-[11px] font-bold text-stone-900 leading-tight truncate">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Ruta del viaje card ── */}
      <div className="mx-3 mt-2 rounded-2xl bg-white border border-stone-200/80 p-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-teal-50 flex items-center justify-center">
              <MapIcon className="w-3.5 h-3.5 text-teal-600" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-stone-900 leading-tight">Ruta del viaje</p>
              <p className="text-[8px] text-stone-400">1 país · 3 ciudades</p>
            </div>
          </div>
          <span className="text-[9px] font-bold text-teal-700">Editar ruta</span>
        </div>
        <div className="flex items-center gap-1.5 mt-1.5 rounded-xl bg-stone-100 px-2 py-1">
          <span className="text-[9px] font-bold text-teal-700 shrink-0">Noruega</span>
          <span className="text-[9px] text-stone-500 truncate">Bergen, Flåm, Geiranger</span>
        </div>
      </div>

      {/* Voice command FAB (amber, like the real app) */}
      <div className="absolute right-3 bottom-14 w-10 h-10 rounded-full bg-amber-500 shadow-[0_8px_20px_-4px_rgba(245,158,11,0.6)] flex items-center justify-center">
        <Mic className="w-4 h-4 text-white" />
      </div>

      {/* ── Bottom nav (Reservas · Documentos · Itinerario · Gastos · Más) ── */}
      <div className="mt-auto grid grid-cols-5 border-t border-stone-200 bg-white px-1 pt-1.5 pb-2.5">
        {[
          { Icon: Bed, label: 'Reservas' },
          { Icon: FileText, label: 'Documentos' },
          { Icon: Calendar, label: 'Itinerario' },
          { Icon: Wallet, label: 'Gastos' },
          { Icon: MoreHorizontal, label: 'Más' },
        ].map(({ Icon, label }) => (
          <div key={label} className="flex flex-col items-center gap-0.5">
            <Icon className="w-4 h-4 text-stone-500" />
            <span className="text-[7px] text-stone-500 font-medium">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* --------------------- Floating mini-cards (Hero) ------------------------ */
const FloatingExpenseCard = () => (
  <div
    className="absolute -left-6 sm:-left-10 lg:-left-16 top-24 sm:top-32 w-44 sm:w-52 rounded-2xl bg-white border border-stone-200 shadow-[0_15px_40px_-15px_rgba(0,0,0,0.18)] p-3 hidden sm:block rotate-[-3deg]"
    data-testid="hero-floating-expenses"
  >
    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-bold mb-1.5">Gastos del viaje</p>
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12">
        <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="4" />
          <circle
            cx="18" cy="18" r="15.9" fill="none" stroke="#14b8a6" strokeWidth="4"
            strokeDasharray="75 25" strokeLinecap="round"
          />
        </svg>
      </div>
      <div>
        <p style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 }} className="text-base text-stone-900">
          1.345,80 €
        </p>
        <p className="text-[10px] text-stone-500">75 % repartido</p>
      </div>
    </div>
  </div>
);

const FloatingVoteCard = () => (
  <div
    className="absolute -right-4 sm:-right-8 lg:-right-14 top-44 sm:top-56 w-52 sm:w-60 rounded-2xl bg-white border border-stone-200 shadow-[0_15px_40px_-15px_rgba(0,0,0,0.18)] p-3.5 hidden sm:block rotate-[3deg]"
    data-testid="hero-floating-vote"
  >
    <p className="text-[10px] uppercase tracking-wider text-orange-600 font-bold mb-1.5">Votación activa</p>
    <p className="text-xs font-semibold text-stone-900 mb-2 leading-tight">¿Qué actividad hacemos el sábado?</p>
    {[
      { label: 'Ruta en barco', pct: 65, votes: '5' },
      { label: 'Visita bodegas', pct: 25, votes: '2' },
    ].map((o) => (
      <div key={o.label} className="mb-1.5 last:mb-0">
        <div className="flex items-center justify-between text-[10px] mb-0.5">
          <span className="text-stone-700 font-medium">{o.label}</span>
          <span className="text-stone-400">{o.votes}</span>
        </div>
        <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full" style={{ width: `${o.pct}%` }} />
        </div>
      </div>
    ))}
  </div>
);

/* ------------------------------ Hero ------------------------------------ */
// Public stats fetcher — drives the live social-proof counter on the hero.
// Cached server-side for 5 min, so this fires at most once per landing visit.
const usePublicStats = () => {
  const [stats, setStats] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/stats`;
    fetch(url).then((r) => r.json()).then((d) => {
      if (!cancelled) setStats(d);
    }).catch(() => { /* silent — fall back to default copy */ });
    return () => { cancelled = true; };
  }, []);
  return stats;
};

const Hero = () => {
  const { t } = useTranslation('landing');
  return (
  <section
    id="hero"
    className="relative isolate overflow-hidden"
    data-testid="landing-hero"
  >
    {/* Dream travel photo background + subtle Ken Burns (CSS-only, GPU-cheap
        "video feel" without the PageSpeed cost of a real video) */}
    <div className="absolute inset-0 -z-10 overflow-hidden bg-stone-900">
      <picture>
        {/* Explicit media query (not srcset+DPR math) so phones always get
            the light -mobile variant — high-DPR phones were downloading the
            134 KB desktop file otherwise. Must match the preload script in
            index.html (innerWidth <= 768). Jul 2026: AVIF variants sourced
            first — 30-40% lighter than WebP on modern browsers, fallback
            keeps working on Firefox <93 and iOS <16. */}
        <source media="(max-width: 768px)" type="image/avif" srcSet={HERO_PICK.mobileAvif} />
        <source media="(max-width: 768px)" srcSet={HERO_PICK.mobile} />
        <source type="image/avif" srcSet={HERO_PICK.srcAvif} />
        <img
          src={HERO_PICK.src}
          alt=""
          fetchPriority="high"
          decoding="async"
          data-hero-variant={HERO_PICK.id}
          className="w-full h-full object-cover tovias-kenburns"
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-r from-stone-950/75 via-stone-950/40 to-stone-950/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 via-transparent to-stone-950/30" />
    </div>
    <style>{`
      @keyframes tovias-kenburns { 0% { transform: scale(1); } 100% { transform: scale(1.09) translate(-0.6%, -0.8%); } }
      .tovias-kenburns { animation: tovias-kenburns 26s ease-in-out infinite alternate; will-change: transform; }
      @media (prefers-reduced-motion: reduce) { .tovias-kenburns { animation: none; } }
    `}</style>

    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-24 pb-20 lg:pb-28">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Left: copy */}
        <div className="text-center lg:text-left min-w-0">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md ring-1 ring-white/25 text-white text-xs font-semibold mb-5">
            <Plane className="w-3.5 h-3.5" /> {t('hero.kicker')}
          </span>
          <h1
            className="text-white leading-[1.05] mb-5 max-w-full drop-shadow-lg"
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 700,
              // Lower bound 1.5rem + tighter vw + word-break ensure the H1 fits
              // even on Android with the largest accessibility font setting
              // (tested at root font-size 22px on 380px viewport).
              fontSize: 'clamp(1.5rem, 5.5vw + .25rem, 3.75rem)',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            {t('hero.titlePart1')} <span className="text-teal-300">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-base sm:text-lg text-white/90 max-w-xl mx-auto lg:mx-0 mb-7 leading-relaxed drop-shadow">
            {t('hero.subtitleLine1')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-7">
            <Link to="/register" data-testid="hero-cta-start">
              <Button className="bg-white hover:bg-teal-50 text-stone-900 rounded-full px-7 py-6 text-base font-bold shadow-lg hover:shadow-xl transition-shadow w-full sm:w-auto">
                {t('hero.ctaPrimary')}
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={async () => {
                const btn = document.querySelector('[data-testid="hero-cta-try-demo"]');
                if (btn) btn.setAttribute('disabled', 'true');
                try {
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/start`, {
                    method: 'POST',
                    credentials: 'include',
                  });
                  if (!res.ok) throw new Error('demo_start_failed');
                  const data = await res.json();
                  // Small delay so the cookie is committed cross-domain in Safari.
                  setTimeout(() => { window.location.href = `/trips/${data.trip_id}?demo=1`; }, 150);
                } catch (e) {
                  if (btn) btn.removeAttribute('disabled');
                  alert('No se pudo iniciar la demo. Prueba de nuevo en unos segundos.');
                }
              }}
              data-testid="hero-cta-try-demo"
              className="rounded-full px-7 py-6 text-base font-semibold border-white/40 bg-white/10 backdrop-blur-md text-white hover:bg-white/20 hover:text-white w-full sm:w-auto"
            >
              <Play className="w-4 h-4 mr-2" /> Prueba sin registro
            </Button>
          </div>
        </div>

        {/* Right: phone + floating cards */}
        <div className="relative h-[560px] sm:h-[640px] flex items-center justify-center">
          {/* Phone */}
          <PhoneMockup />
          {/* Floating product cards */}
          <FloatingExpenseCard />
          <FloatingVoteCard />
        </div>
      </div>
    </div>
  </section>
  );
};

/* ------------------------- Magia (data entry) --------------------------- */
// Minimalist 3-column block: the three effortless ways data gets into a trip.
const MAGIC_METHODS = [
  {
    num: '01',
    Icon: Zap,
    title: 'Automático',
    desc: 'Activa el reenvío automático una sola vez y cada confirmación que llegue a tu correo aparecerá en el viaje sin que muevas un dedo.',
  },
  {
    num: '02',
    Icon: Mail,
    title: 'Reenvía un email',
    desc: 'Te llega una reserva, la reenvías a tu dirección personal de Tovías y la IA extrae fechas, importe y lugar en segundos.',
    pill: 'tu-nombre@tovias.app',
  },
  {
    num: '03',
    Icon: PenLine,
    title: 'A mano, si quieres',
    desc: 'Control total de siempre: añade y edita cualquier reserva, gasto o plan tú mismo, tan rápido como escribir una nota.',
  },
];

const MagicSection = () => (
  <div data-testid="landing-magic-section">
    <div className="max-w-2xl">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold mb-4">
        <Wand2 className="w-3.5 h-3.5" /> La magia
      </span>
      <h2
        className="text-stone-900 mb-3"
        style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.6rem, 3.2vw, 2.5rem)', lineHeight: 1.1 }}
      >
        Reenvía un email y tu viaje se organiza solo
      </h2>
      <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
        Tres formas de llenar tu viaje, de totalmente automático a control absoluto. Cero esfuerzo.
      </p>
    </div>

    <div className="mt-10 grid sm:grid-cols-3 gap-8 sm:gap-6 lg:gap-10">
      {MAGIC_METHODS.map((m) => (
        <div key={m.num} className="border-t border-stone-200 pt-6" data-testid={`magic-method-${m.num}`}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-4xl font-light text-stone-300 tabular-nums select-none" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              {m.num}
            </span>
            <m.Icon className="w-5 h-5 text-teal-600" />
          </div>
          <h3 className="mt-4 text-stone-900 font-bold text-lg">{m.title}</h3>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed">{m.desc}</p>
          {m.pill && (
            <span className="mt-3 inline-block font-mono text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded-full px-3 py-1.5">
              {m.pill}
            </span>
          )}
        </div>
      ))}
    </div>

    <Link
      to="/register"
      data-testid="magic-section-cta"
      className="mt-10 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors"
    >
      Probar la magia <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);

/* ------------------------ Reviews (real testimonials) ------------------- */
// Circular avatar with real photo when available, initials fallback otherwise.
const AVATAR_COLORS = ['#0f766e', '#b45309', '#9333ea', '#0369a1', '#be123c', '#4d7c0f'];
const ReviewAvatar = ({ name, photoUrl, size = 40 }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const color = AVATAR_COLORS[(name || '').length % AVATAR_COLORS.length];
  if (photoUrl) {
    return (
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
        alt={name}
        loading="lazy"
        style={{ width: size, height: size }}
        className="rounded-full object-cover ring-1 ring-stone-200 flex-shrink-0"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, background: color }}
      className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
    >
      {initial}
    </div>
  );
};

// Renders ONLY when there are approved real reviews (collected post-NPS).
// With >= 5 reviews we also emit aggregateRating JSON-LD linked by @id to
// the static SoftwareApplication schema in index.html — legit rich results.
const ReviewsSection = () => {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/reviews?limit=9`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  if (!data || !data.items || data.items.length === 0) return null;

  const jsonLd = data.count >= 5 && data.average
    ? JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        '@id': 'https://tovias.app/#software',
        name: 'TOVÍAS',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: String(data.average),
          ratingCount: String(data.count),
          bestRating: '5',
          worstRating: '1',
        },
      })
    : null;

  return (
    <section className="py-20 lg:py-24 bg-stone-50" data-testid="landing-reviews-section">
      {jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white ring-1 ring-stone-200 text-stone-700 text-xs font-semibold mb-4">
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /> Reseñas reales
          </span>
          <h2
            className="text-stone-900"
            style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.6rem, 3.2vw, 2.5rem)', lineHeight: 1.1 }}
          >
            Lo que dicen los viajeros
          </h2>
          {data.average && (
            <p className="mt-2 text-stone-600 text-sm" data-testid="reviews-average">
              <strong className="text-stone-900">{data.average}</strong> de 5 · {data.count} reseña{data.count === 1 ? '' : 's'} verificada{data.count === 1 ? '' : 's'} tras el viaje
            </p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.items.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-2xl border border-stone-200 p-5 flex flex-col"
              data-testid={`review-card-${r.id}`}
            >
              <div className="flex mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-4 h-4 ${n <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`}
                  />
                ))}
              </div>
              <p className="text-sm text-stone-700 leading-relaxed flex-1">“{r.comment}”</p>
              <div className="mt-4 flex items-center gap-2.5">
                <ReviewAvatar name={r.name} photoUrl={r.photo_url} size={36} />
                <p className="text-xs text-stone-500 flex items-center gap-1.5">
                  <strong className="text-stone-800">{r.name}</strong>
                  {r.destination_country ? (
                    <>
                      <span>·</span>
                      {r.country_code && (
                        <img
                          src={`https://flagcdn.com/w40/${r.country_code}.png`}
                          alt={r.destination_country}
                          loading="lazy"
                          className="w-4 h-3 rounded-[2px] object-cover ring-1 ring-stone-200 inline-block"
                        />
                      )}
                      <span>Viaje a {r.destination_country}</span>
                    </>
                  ) : null}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- Compact reviews carousel (near install CTA) -------
 * feb 28 msg 824 · Reyes · Un carrusel horizontal discreto que aparece
 * justo debajo del banner de instalación (AppShowcase). Refuerza la
 * confianza con reseñas reales en el momento exacto en que el usuario
 * decide instalar. Reutiliza `/api/public/reviews` (mismo endpoint que
 * `ReviewsSection`) y ordena por rating desc + created_at desc para
 * mostrar primero las 5★ más frescas. Ratio 4/5/6 estrellas se rehúsa
 * porque el endpoint sólo devuelve reseñas aprobadas ≥ 3★.
 * ------------------------------------------------------------------- */
const ReviewsCarousel = () => {
  const [data, setData] = React.useState(null);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/reviews?limit=12`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-review-card]')?.offsetWidth || 320;
    el.scrollBy({ left: dir * (cardWidth + 16), behavior: 'smooth' });
  };

  if (!data || !data.items || data.items.length === 0) return null;

  return (
    <section
      className="pt-6 pb-16 sm:pt-8 sm:pb-20"
      data-testid="landing-reviews-carousel"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white ring-1 ring-stone-200 text-stone-700 text-[11px] font-semibold mb-2 shadow-sm">
              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
              {data.average ? `${data.average} de 5 · ${data.count} reseñas verificadas` : 'Reseñas verificadas'}
            </div>
            <h3
              className="text-stone-900 leading-tight"
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 700,
                fontSize: 'clamp(1.25rem, 2.4vw, 1.75rem)',
              }}
            >
              Lo que dicen viajeros reales tras volver
            </h3>
          </div>
          {/* Controles de scroll — solo visibles ≥sm porque en móvil el
              swipe táctil es más natural que dos flechas robando espacio. */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="w-9 h-9 rounded-full bg-white ring-1 ring-stone-200 hover:bg-stone-50 hover:-translate-y-0.5 transition-all flex items-center justify-center text-stone-700 shadow-sm"
              aria-label="Anterior reseña"
              data-testid="reviews-carousel-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="w-9 h-9 rounded-full bg-white ring-1 ring-stone-200 hover:bg-stone-50 hover:-translate-y-0.5 transition-all flex items-center justify-center text-stone-700 shadow-sm"
              aria-label="Siguiente reseña"
              data-testid="reviews-carousel-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth reviews-carousel-track"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <style>{`
            .reviews-carousel-track::-webkit-scrollbar { display: none; }
          `}</style>
          {data.items.map((r) => (
            <article
              key={r.id}
              data-review-card
              data-testid={`review-carousel-card-${r.id}`}
              className="snap-start shrink-0 w-[280px] sm:w-[320px] bg-white rounded-2xl ring-1 ring-stone-200 p-5 flex flex-col hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              <div className="flex mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-3.5 h-3.5 ${n <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`}
                  />
                ))}
              </div>
              <p className="text-sm text-stone-700 leading-relaxed flex-1 line-clamp-5">
                “{r.comment}”
              </p>
              <div className="mt-4 flex items-center gap-2">
                <ReviewAvatar name={r.name} photoUrl={r.photo_url} size={32} />
                <p className="text-xs text-stone-500 flex items-center gap-1.5">
                  <strong className="text-stone-800">{r.name}</strong>
                  {r.destination_country && (
                    <>
                      <span>·</span>
                      {r.country_code && (
                        <img
                          src={`https://flagcdn.com/w40/${r.country_code}.png`}
                          alt=""
                          loading="lazy"
                          className="w-3.5 h-2.5 rounded-[2px] object-cover ring-1 ring-stone-200 inline-block"
                        />
                      )}
                      <span>{r.destination_country}</span>
                    </>
                  )}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};



/* --------------------- Feature row (alternating) ------------------------ */
// Internal-SEO links: each row deep-links to its /funciones/* page.
const FEATURE_ROW_LINKS = {
  'feature-collaboration': { to: '/funciones/votaciones', label: 'Ver cómo funcionan las votaciones de grupo' },
  'feature-expenses': { to: '/funciones/gastos', label: 'Ver cómo se dividen los gastos' },
  'feature-itinerary': { to: '/funciones/itinerario', label: 'Ver cómo se planifica el itinerario' },
  'feature-documents': { to: '/funciones/documentos', label: 'Ver cómo se guardan los documentos del viaje' },
  'feature-ticket-scan': { to: '/funciones/reservas', label: 'Ver cómo se importan las reservas' },
  'feature-cross-device': { to: '/funciones', label: 'Ver todas las funciones de Tovías' },
};

const FeatureRow = ({
  reverse = false,
  eyebrow,
  Icon,
  title,
  description,
  bullets = [],
  image,
  children,
  testid,
}) => (
  <div
    className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
    data-testid={testid}
  >
    {/* Text */}
    <div className={`${reverse ? 'lg:order-2' : ''}`}>
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold mb-4">
        <Icon className="w-3.5 h-3.5" /> {eyebrow}
      </div>
      <h2
        className="text-stone-900 leading-tight mb-4"
        style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontWeight: 700,
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
        }}
      >
        {title}
      </h2>
      <p className="text-stone-600 leading-relaxed mb-5">{description}</p>
      {bullets.length > 0 && (
        <ul className="space-y-2">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2.5 text-stone-700 text-sm">
              <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {FEATURE_ROW_LINKS[testid] && (
        <Link
          to={FEATURE_ROW_LINKS[testid].to}
          className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors group"
          data-testid={`${testid}-learn-more`}
        >
          {FEATURE_ROW_LINKS[testid].label}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      )}
    </div>
    {/* Visual */}
    <div className={`relative ${reverse ? 'lg:order-1' : ''}`}>
      {image && (
        <img
          src={image}
          alt=""
          loading="lazy"
          className="rounded-3xl w-full aspect-[4/3] object-cover shadow-xl"
        />
      )}
      {children}
    </div>
  </div>
);

/* -------------- Feature row "mockup" overlay cards ---------------------- */
const MembersMockup = () => (
  <div className="absolute -bottom-6 right-4 lg:right-8 w-56 sm:w-64 rounded-2xl bg-white border border-stone-200 shadow-2xl p-4">
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs font-bold text-stone-900">Miembros del viaje</p>
      <button className="text-[11px] text-teal-700 font-semibold inline-flex items-center gap-1">
        <Plus className="w-3 h-3" /> Invitar
      </button>
    </div>
    {[
      { name: 'Tú (Organizador)', img: '/landing/avatars/avatar-tu.webp' },
      { name: 'María', img: '/landing/avatars/avatar-maria.webp' },
      { name: 'Carlos', img: '/landing/avatars/avatar-carlos.webp' },
      { name: 'Lucía', img: '/landing/avatars/avatar-lucia.webp' },
      { name: 'Andrés', img: '/landing/avatars/avatar-andres.webp' },
    ].map((m, i) => (
      <div key={i} className="flex items-center gap-2.5 py-1.5 border-b border-stone-100 last:border-0">
        <img
          src={m.img}
          alt={m.name}
          loading="lazy"
          decoding="async"
          className="w-7 h-7 rounded-full object-cover ring-1 ring-stone-200"
        />
        <span className="text-xs text-stone-700">{m.name}</span>
      </div>
    ))}
  </div>
);

const ExpenseMockup = () => (
  <div className="absolute -top-4 -left-4 lg:-left-8 w-60 sm:w-72 rounded-2xl bg-white border border-stone-200 shadow-2xl p-4">
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs font-bold text-stone-900">Gastos compartidos</p>
      <span className="text-[10px] text-stone-400">Junio</span>
    </div>
    <div className="flex gap-1.5 mb-3">
      {['Todos', 'Pagado', 'Pendiente'].map((t, i) => (
        <span
          key={t}
          className={`text-[10px] px-2 py-0.5 rounded-full ${
            i === 0 ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-stone-500'
          }`}
        >
          {t}
        </span>
      ))}
    </div>
    {[
      { name: 'Alquiler de coches', who: 'Carlos', amount: '120,00 €' },
      { name: 'Cena del viernes', who: 'María', amount: '85,40 €' },
      { name: 'Entradas concierto', who: 'Tú', amount: '60,00 €' },
      { name: 'Brunch domingo', who: 'Lucía', amount: '35,10 €' },
    ].map((e, i) => (
      <div key={i} className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0">
        <div>
          <p className="text-xs font-semibold text-stone-800 leading-tight">{e.name}</p>
          <p className="text-[10px] text-teal-700">Pagado por {e.who}</p>
        </div>
        <span className="text-xs font-bold text-stone-900 tabular-nums">{e.amount}</span>
      </div>
    ))}
    <div className="flex items-center justify-between pt-2.5 mt-1 border-t border-stone-200">
      <span className="text-xs font-semibold text-stone-700">Tu balance</span>
      <span className="text-sm font-bold text-emerald-600 tabular-nums">+ 45,20 €</span>
    </div>
  </div>
);

const ItineraryMockup = () => (
  <div className="absolute -bottom-4 left-4 lg:left-8 w-60 sm:w-72 rounded-2xl bg-white border border-stone-200 shadow-2xl p-4">
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs font-bold text-stone-900">Itinerario</p>
      <span className="text-[10px] text-teal-700 font-semibold">Ver calendario</span>
    </div>
    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-bold mb-1.5">Jue 13 jun</p>
    <div className="relative pl-4 border-l-2 border-teal-200 space-y-2 mb-3">
      <div>
        <p className="text-xs font-semibold text-stone-800">10:00 · Recogida coches</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-stone-800">20:30 · Sunset Sa Foradada</p>
      </div>
    </div>
    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-bold mb-1.5">Vie 14 jun</p>
    <div className="relative pl-4 border-l-2 border-orange-200 space-y-2">
      <div>
        <p className="text-xs font-semibold text-stone-800">12:00 · Cala Mondragó</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-stone-800">21:00 · Cena en grupo</p>
      </div>
    </div>
  </div>
);

const DocsMockup = () => (
  <div className="absolute -top-4 right-4 lg:right-8 w-60 sm:w-72 rounded-2xl bg-white border border-stone-200 shadow-2xl p-4">
    <p className="text-xs font-bold text-stone-900 mb-3">Documentos importantes</p>
    {[
      { name: 'Pasaportes', type: 'PDF', color: '#fb7185' },
      { name: 'Reserva vuelos', type: 'PDF', color: '#60a5fa' },
      { name: 'Seguro de viaje', type: 'PDF', color: '#34d399' },
      { name: 'Reserva hotel', type: 'PDF', color: '#fbbf24' },
    ].map((d, i) => (
      <div key={i} className="flex items-center gap-2.5 py-1.5 border-b border-stone-100 last:border-0">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center text-[9px] font-bold text-white"
          style={{ background: d.color }}
        >
          PDF
        </div>
        <span className="text-xs text-stone-700 flex-1">{d.name}</span>
        <span className="text-[10px] text-stone-400">{d.type}</span>
      </div>
    ))}
  </div>
);

/* ------------------------ Pricing teaser -------------------------------- */
const PricingTeaser = () => {
  // Each plan card links to the correct destination:
  // - Free → /register (signing up gives you the free plan)
  // - Viajero/Grupo → /register too (must register before paying);
  //   logged users will be redirected to /subscription via Stripe afterwards.
  const PLANS = [
    {
      key: 'free',
      name: 'Free',
      price: '0 €',
      sub: 'Para probar',
      perks: [
        '1 viaje organizado · hasta 5 participantes',
        '100 MB de almacenamiento',
        'Itinerario, gastos, mapa y galería',
        'OCR de tickets y detección de reservas por email',
        'IA · 10 preguntas/día · 1 viaje generado',
        'Alertas de seguridad en ruta',
      ],
      cta: 'Empieza gratis',
      to: '/register',
      featured: false,
    },
    {
      key: 'explorador',
      name: 'Explorador',
      price: '8 €',
      sub: 'suscripción anual',
      perks: [
        'Todo lo de Free, más:',
        '3 viajes organizados · 8 participantes · 0,5 GB',
        'IA · 50 preguntas/día · generador ilimitado',
        'Alertas de tráfico DGT en tu ruta',
      ],
      cta: 'Elegir Explorador',
      to: '/register?plan=explorador',
      featured: false,
    },
    {
      key: 'viajero',
      name: 'Viajero',
      price: '29,99 €',
      sub: 'suscripción anual',
      perks: [
        'Todo lo de Explorador, más:',
        'Viajes ilimitados · 30 participantes · 2 GB',
        'IA ilimitada · añade planes hablando por voz',
        'Alertas de vuelo, tráfico DGT e incendios',
        'Recomendaciones IA con Foursquare Pro · descuentos en impresión',
      ],
      cta: 'Elegir Viajero',
      to: '/register?plan=viajero',
      featured: true,
    },
    {
      key: 'grupo',
      name: 'Grupo',
      price: '69,99 €',
      sub: 'suscripción anual',
      perks: [
        'Todo lo de Viajero, más:',
        'Viajes ilimitados · 60 participantes · 10 GB',
        'IA prioritaria · respuestas más rápidas',
        'Soporte prioritario + onboarding en vídeo',
        'Ideal bodas, empresa y grupos grandes',
      ],
      cta: 'Elegir Grupo',
      to: '/register?plan=grupo',
      featured: false,
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-stone-50 border-y border-stone-200" data-testid="pricing-teaser">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2
          className="text-stone-900 leading-tight mb-3"
          style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}
        >
          Planes para cualquier tipo de viajero
        </h2>
        <p className="text-stone-600 mb-10 max-w-2xl mx-auto">
          Desde escapadas de fin de semana hasta grandes aventuras alrededor del mundo.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8 text-left">
          {PLANS.map((p) => (
            <Link
              key={p.key}
              to={p.to}
              data-testid={`pricing-card-${p.key}`}
              className={`group rounded-3xl p-6 border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                p.featured
                  ? 'border-teal-500 bg-white shadow-xl sm:scale-[1.02] hover:shadow-2xl'
                  : 'border-stone-200 bg-white hover:border-teal-300'
              }`}
            >
              {p.featured && (
                <span className="inline-block px-2.5 py-0.5 rounded-full bg-teal-100 text-teal-700 text-[10px] font-bold uppercase tracking-wider mb-3">
                  Más popular
                </span>
              )}
              <p className="text-sm font-semibold text-stone-500 mb-1">{p.name}</p>
              <p className="mb-1 leading-tight">
                <span style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 }} className="block text-3xl text-stone-900 whitespace-nowrap">
                  {p.price}
                </span>
                <span className="block text-[11px] text-stone-500 mt-0.5">{p.sub}</span>
              </p>
              <ul className="mt-4 space-y-1.5 mb-5">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-xs text-stone-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 mt-0.5 shrink-0" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3.5 py-2 transition-colors ${
                  p.featured
                    ? 'bg-teal-600 text-white group-hover:bg-teal-700'
                    : 'bg-stone-100 text-stone-700 group-hover:bg-stone-900 group-hover:text-white'
                }`}
              >
                {p.cta} →
              </span>
            </Link>
          ))}
        </div>

        <p className="text-[11px] text-stone-400 mb-6 max-w-xl mx-auto">
          * Cuentan los viajes que TÚ organizas (aunque los archives). Ser invitado en un viaje de otra persona no consume tu límite.
        </p>

        <Link to="/planes" data-testid="pricing-teaser-link">
          <Button variant="outline" className="rounded-full border-stone-300 hover:bg-white px-6 font-semibold">
            Ver todos los precios →
          </Button>
        </Link>
      </div>
    </section>
  );
};

/* ---------------------- Install / Get the App -------------------------- */

const InstallSection = () => {
  return (
    <section
      id="instalar"
      className="py-20 lg:py-28 bg-gradient-to-br from-teal-50 via-white to-orange-50"
      data-testid="install-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white ring-1 ring-stone-200 text-stone-700 text-xs font-semibold mb-5">
          <Smartphone className="w-3.5 h-3.5 text-teal-700" /> Empieza ya
        </div>
        <h2
          className="text-stone-900 leading-tight mb-3"
          style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
        >
          Crea tu cuenta gratis
        </h2>
        <p className="text-stone-600 mb-9 max-w-2xl mx-auto">
          Tovías funciona en cualquier dispositivo desde el navegador, y ya puedes descargarla en Google Play.
        </p>

        {/* Primary CTA — sign up */}
        <Link to="/register" data-testid="install-primary-cta">
          <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 py-6 text-base font-bold shadow-lg hover:shadow-xl mb-2">
            Crear cuenta gratis →
          </Button>
        </Link>
        <p className="text-[11px] text-stone-500 mb-8">
          Sin tarjeta de crédito · cancelas cuando quieras
        </p>

        {/* Always-visible install button (PWA) */}
        <div className="pt-6 border-t border-stone-200 max-w-md mx-auto">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-stone-400 mb-3">
            Llévala en tu móvil
          </p>
          <div className="flex flex-wrap gap-3 justify-center items-center">
            <GetTheAppButton className="h-12" />
          </div>
          <p className="text-[11px] text-stone-500 mt-3">
            Android en Google Play · iPhone muy pronto en App Store.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ----------------------------- Final CTA -------------------------------- */
const FinalCTA = () => (
  <section className="py-20 lg:py-28" data-testid="final-cta-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="rounded-[2rem] bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-800 p-10 sm:p-14 text-center text-white shadow-2xl relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-12 w-72 h-72 rounded-full bg-orange-300/15 blur-3xl pointer-events-none" />
        <h2
          className="leading-tight mb-3 relative"
          style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.875rem, 4vw, 3rem)' }}
        >
          Tu próxima aventura empieza aquí
        </h2>
        <p className="text-teal-50/90 mb-7 max-w-xl mx-auto relative">
          Únete a los primeros viajeros que están organizando sus viajes sin estrés.
        </p>
        <Link to="/register" data-testid="final-cta-start">
          <Button className="bg-white text-teal-700 hover:bg-stone-50 rounded-full px-8 py-6 text-base font-bold shadow-lg">
            Empieza gratis →
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

/* ------------------------- 3 fases del viaje ----------------------------- */
// La app se estructura en 3 fases (antes / durante / después). Esta tira las
// presenta de un vistazo y ancla a cada bloque de funciones de la landing.
const PHASES = [
  {
    id: 'fase-antes',
    n: '01',
    icon: Vote,
    tint: 'bg-teal-50 text-teal-700',
    ring: 'hover:border-teal-300',
    title: 'Antes del viaje',
    sub: 'Organizadlo juntos',
    items: ['Reenvía un email y se organiza solo', 'Votaciones y avisos del grupo', 'Presupuesto y calendario'],
  },
  {
    id: 'fase-durante',
    n: '02',
    icon: Compass,
    tint: 'bg-amber-50 text-amber-700',
    ring: 'hover:border-amber-300',
    title: 'Durante el viaje',
    sub: 'Vividlo sin caos',
    items: ['Itinerario y mapa del día', 'Dónde quedar y entradas a mano', 'Gastos al instante con una foto'],
  },
  {
    id: 'fase-despues',
    n: '03',
    icon: BookOpen,
    tint: 'bg-rose-50 text-rose-700',
    ring: 'hover:border-rose-300',
    title: 'Después del viaje',
    sub: 'Revividlo y compartidlo',
    items: ['Vídeo-resumen y libro de recuerdos', 'Comparte la ruta con un enlace', 'Cuentas saldadas por Bizum'],
  },
];

const PhasesStrip = () => (
  <section className="py-16 lg:py-20 bg-stone-50" data-testid="phases-strip">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2
        className="text-stone-900 mb-8 max-w-2xl"
        style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2.1rem)', lineHeight: 1.15 }}
      >
        Tovías te acompaña antes, durante y después del viaje
      </h2>
      <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
        {PHASES.map((p) => (
          <a
            key={p.id}
            href={`#${p.id}`}
            className={`group rounded-3xl bg-white border border-stone-200 p-6 lg:p-7 transition-all hover:shadow-lg ${p.ring}`}
            data-testid={`phase-card-${p.id}`}
          >
            <div className="flex items-center justify-between mb-4">
              <span className={`inline-flex w-11 h-11 rounded-2xl items-center justify-center ${p.tint}`}>
                <p.icon className="w-5 h-5" />
              </span>
              <span className="text-3xl font-bold text-stone-200 group-hover:text-stone-300 transition-colors" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>{p.n}</span>
            </div>
            <h3 className="font-bold text-stone-900 text-lg leading-tight">{p.title}</h3>
            <p className="text-sm text-stone-500 mb-3">{p.sub}</p>
            <ul className="space-y-1.5">
              {p.items.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-stone-600">
                  <Check className="w-3.5 h-3.5 text-teal-600 mt-0.5 flex-shrink-0" />
                  {it}
                </li>
              ))}
            </ul>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 mt-4 group-hover:gap-2 transition-all">
              Ver cómo <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </a>
        ))}
      </div>
    </div>
  </section>
);

// Cabecera de fase dentro de la sección de funciones
const PhaseHeader = ({ id, n, title, sub }) => (
  <div id={id} className="scroll-mt-24 pt-4" data-testid={`phase-header-${id}`}>
    <div className="flex items-center gap-4">
      <span
        className="text-5xl lg:text-6xl font-bold text-stone-200 leading-none select-none"
        style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
      >
        {n}
      </span>
      <div>
        <p className="text-xs font-bold uppercase tracking-[3px] text-teal-700">{`Fase ${Number(n)}`}</p>
        <h2
          className="text-stone-900"
          style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2.1rem)', lineHeight: 1.15 }}
        >
          {title}
        </h2>
        <p className="text-stone-500 text-sm md:text-base mt-0.5">{sub}</p>
      </div>
    </div>
    <div className="h-px bg-gradient-to-r from-stone-200 to-transparent mt-6" />
  </div>
);

/* ------------------------------ Footer ---------------------------------- */
// Footer extracted to <SiteFooter /> for reuse across all public pages
// (Landing, Press, Ambassadors, Status, FAQs, …). Edit it once there.
import SiteFooter from '@/components/SiteFooter';
const Footer = SiteFooter;
import ActiveEmbajadoresSection from '../components/showcase/ActiveEmbajadoresSection';

/* ----------------------------- Page ------------------------------------- */
export default function Landing() {
  return (
    <div className="bg-white" data-testid="landing-page">
      <SEO
        title="Organiza viajes en grupo sin caos de WhatsApp"
        description="Organiza tus viajes con amigos en una sola app. Itinerarios compartidos, control de gastos estilo Splitwise, reservas y fotos. Empieza gratis."
        path="/"
      />
      {/* Organization + SoftwareApplication JSON-LD live statically in
          public/index.html so non-JS crawlers (AI bots) can read them. */}
      <Header />
      <Hero />

      {/* Animated phone screens showcase */}
      <AppShowcase />

      {/* Las 3 fases del viaje — la estructura de la app de un vistazo */}
      <PhasesStrip />

      {/* Rutas con audioguía — bloque compacto y claro, tras las 3 fases
          (oct 2026: el usuario no quiere que las rutas "se coman" la portada) */}
      <RoutesSpotlight />

      <WhatsNew />

      {/* Feature rows */}
      <section id="funciones" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 lg:space-y-32">
          <PhaseHeader id="fase-antes" n="01" title="Antes del viaje" sub="Organizadlo juntos: decisiones, reservas y presupuesto sin caos de WhatsApp." />
          <FeatureRow
            testid="feature-collaboration"
            eyebrow="Colaboración"
            Icon={Users}
            title="Colabora con tu grupo"
            description="Invita a tus amigos y decidid juntos los detalles. Votad destinos, fechas y actividades en tiempo real, con comentarios y avisos para que nada se quede en el aire."
            bullets={['Invitaciones por enlace', 'Votaciones y comentarios', 'Permisos por rol']}
            image={GROUP_IMAGE}
          >
            <MembersMockup />
          </FeatureRow>

          <FeatureRow
            reverse
            testid="feature-documents"
            eyebrow="Reservas & documentos"
            Icon={FolderOpen}
            title="Guarda todo lo importante"
            description="Billetes, reservas, PDFs y confirmaciones. Todo accesible para cualquier miembro del grupo, incluso sin conexión."
            bullets={['PDFs y fotos según tu plan', 'Reservas importadas desde tu email', 'Compartir con un enlace']}
            image={DOCS_IMAGE}
          >
            <DocsMockup />
          </FeatureRow>

          {/* "Magia" — reenvía un email y el viaje se organiza solo.
              Vive en la fase ANTES: es parte de la organización previa. */}
          <MagicSection />

          <FeatureRow
            testid="feature-calendar-sync"
            eyebrow="Tu agenda, sincronizada"
            Icon={Calendar}
            title="Por placer o por trabajo, Tovías va contigo"
            description="Sea cual sea el motivo de tu viaje, Tovías es tu mejor compañero: sincroniza el itinerario con tu calendario y verás cada vuelo, hotel y plan junto a tus reuniones, citas médicas y el resto de tu vida. Si el viaje cambia, tu agenda se actualiza sola."
            bullets={['Google Calendar, Apple y Outlook', 'Vuelos, hoteles y actividades junto a tus citas', 'Comparte tu itinerario con un clic']}
            image="/landing/calendar-sync.webp"
          />

          <FeatureRow
            reverse
            testid="feature-cross-device"
            eyebrow="Funciona donde estés"
            Icon={Smartphone}
            title="Planifica en el ordenador, viaja con el móvil"
            description="Toda la lógica pesada (presupuesto, mapas, repartos de pago) la haces cómoda desde el portátil en casa. El día del viaje, abres Tovías en el móvil y todo está sincronizado al segundo — sin exportar, sin emails, sin perder nada."
            bullets={['Sincronización en tiempo real', 'Misma cuenta en todos los dispositivos', 'Instalable como app sin App Store']}
          >
            <div
              className="aspect-[4/3] rounded-3xl shadow-xl overflow-hidden relative bg-gradient-to-br from-sky-50 via-teal-50 to-emerald-50 flex items-center justify-center"
              data-testid="feature-cross-device-visual"
            >
              {/* Laptop mockup — real Tovías UI screenshot inside the frame */}
              <div className="absolute left-[8%] top-[18%] w-[55%]">
                <div className="bg-stone-900 rounded-t-lg px-2 pt-2 pb-1 shadow-2xl">
                  <div className="bg-white rounded-md aspect-[16/10] relative overflow-hidden">
                    <div className="absolute top-1.5 left-1.5 z-10 flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    </div>
                    <img
                      src="/landing/ui/tovias-desktop.webp"
                      alt="Tovías en el navegador — catálogo de rutas con portadas reales"
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="bg-stone-700 h-1.5 rounded-b-lg" />
                <div className="bg-stone-600 h-0.5 w-[40%] mx-auto" />
              </div>
              {/* Sync arrows */}
              <div className="absolute right-[28%] top-[42%] flex flex-col items-center gap-1 text-teal-600 text-[10px] font-bold">
                <div className="flex items-center gap-0.5 animate-pulse">
                  <span>→</span><span>→</span>
                </div>
                <span className="text-[9px]">SYNC</span>
                <div className="flex items-center gap-0.5 animate-pulse">
                  <span>←</span><span>←</span>
                </div>
              </div>
              {/* Phone mockup — same Tovías, mobile view */}
              <div className="absolute right-[8%] top-[14%] w-[24%] bg-stone-900 rounded-2xl p-1.5 shadow-2xl">
                <div className="bg-white rounded-xl aspect-[9/16] relative overflow-hidden">
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 w-6 h-1 bg-stone-900 rounded-full" />
                  <img
                    src="/landing/ui/tovias-mobile.webp"
                    alt="Tovías en el móvil — preview pública de una ruta"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              {/* Caption */}
              <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur ring-1 ring-stone-200 rounded-xl px-3 py-2 shadow-lg text-[11px] text-stone-700 text-center">
                <strong className="text-stone-900">Mismo viaje. Cualquier dispositivo.</strong>
              </div>
            </div>
          </FeatureRow>

          <PhaseHeader id="fase-durante" n="02" title="Durante el viaje" sub="Todo a mano: el plan del día, dónde quedar y los gastos apuntados al momento." />

          <FeatureRow
            testid="feature-itinerary"
            eyebrow="Itinerario"
            Icon={MapIcon}
            title="Itinerario en un vistazo"
            description="Un plan de viaje visual y colaborativo. Todos saben dónde y a qué hora es la próxima aventura. Incluye mapa interactivo de todos los destinos."
            bullets={['Calendario por días', 'Mapa con rutas y paradas', 'Recordatorios automáticos']}
          >
            <div
              className="aspect-[4/3] rounded-3xl shadow-xl overflow-hidden relative"
              style={{
                background: 'linear-gradient(135deg, #ecfeff 0%, #e0f2fe 60%, #fef3c7 100%)',
              }}
            >
              {/* Stylized map */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 300" preserveAspectRatio="none">
                <path
                  d="M 50 200 C 100 120, 180 150, 230 100 S 360 80, 380 60"
                  stroke="#14b8a6"
                  strokeWidth="3"
                  strokeDasharray="6 8"
                  fill="none"
                  strokeLinecap="round"
                />
                {[{ x: 50, y: 200 }, { x: 175, y: 130 }, { x: 280, y: 95 }, { x: 380, y: 60 }].map((p, i) => (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r="9" fill="#F97316" />
                    <circle cx={p.x} cy={p.y} r="3" fill="white" />
                  </g>
                ))}
              </svg>
            </div>
            <ItineraryMockup />
          </FeatureRow>

          <FeatureRow
            reverse
            testid="feature-ticket-scan"
            eyebrow="Gastos al instante"
            Icon={Receipt}
            title="Haz una foto al ticket y listo"
            description="Saca la cámara, encuadras el recibo y Tovías reconoce el total, la moneda y la fecha por ti. Se añade al viaje en 2 segundos — sin escribir números a mano mientras te tomas el café."
            bullets={['OCR multilingüe del importe y moneda', 'Detección de fecha y comercio', 'Edita lo que quieras antes de guardar']}
          >
            <div
              className="aspect-[4/3] rounded-3xl shadow-xl overflow-hidden relative bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 flex items-center justify-center"
              data-testid="feature-ticket-scan-visual"
            >
              {/* Stylised camera-on-ticket mock */}
              <div className="relative w-[58%] aspect-[3/4] rounded-2xl bg-white shadow-2xl border border-stone-200 p-3 rotate-[-6deg]">
                <div className="text-[8px] uppercase tracking-[3px] text-stone-400 font-bold text-center mb-1">Café del puerto</div>
                <div className="border-t border-dashed border-stone-300 my-1.5" />
                <div className="space-y-0.5 text-[9px] text-stone-600">
                  <div className="flex justify-between"><span>2× Café con leche</span><span>4,40 €</span></div>
                  <div className="flex justify-between"><span>1× Croissant</span><span>2,30 €</span></div>
                  <div className="flex justify-between"><span>1× Zumo natural</span><span>3,50 €</span></div>
                </div>
                <div className="border-t border-stone-300 my-1.5" />
                <div className="flex justify-between text-[11px] font-bold text-stone-900">
                  <span>TOTAL</span><span>10,20 €</span>
                </div>
                <div className="text-[7px] text-stone-400 text-center mt-1.5">04/05/2026 · 11:42</div>
              </div>
              {/* Bracket overlay (camera viewfinder) */}
              <div className="absolute inset-[18%] pointer-events-none">
                <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-teal-500 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-teal-500 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-teal-500 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-teal-500 rounded-br-lg" />
              </div>
              {/* "Detected" toast */}
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur ring-1 ring-stone-200 rounded-xl px-3 py-2 shadow-lg flex items-center gap-2 text-[11px]">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 truncate">10,20 € · Café del puerto</div>
                  <div className="text-stone-500 text-[10px]">Añadido a Gastos · esperando tu confirmación</div>
                </div>
              </div>
            </div>
          </FeatureRow>

          {/* "Magia" — las 3 formas de meter datos, debajo del bloque OCR */}
          <FeatureRow
            testid="feature-expenses"
            eyebrow="Gastos compartidos"
            Icon={Wallet}
            title="Controla los gastos sin líos"
            description="Registra quién ha pagado qué. Tovías calcula automáticamente las deudas y te dice quién debe a quién, estilo Splitwise. Cierre del viaje con un solo click."
            bullets={['División equitativa o personalizada', 'Conversión de moneda', 'Resumen por persona y por día']}
            image={CAFE_IMAGE}
          >
            <ExpenseMockup />
          </FeatureRow>

          <PhaseHeader id="fase-despues" n="03" title="Después del viaje" sub="El viaje no acaba al volver: revividlo, compartidlo y cerrad las cuentas sin perseguir a nadie." />

          <FeatureRow
            testid="feature-memories"
            eyebrow="Recuerdos"
            Icon={BookOpen}
            title="Revive el viaje y compártelo"
            description="Al volver, Tovías convierte vuestro viaje en un vídeo-resumen animado con la ruta y las fotos, un diario listo para imprimir como libro, y un enlace precioso para que cualquiera copie la ruta."
            bullets={['Vídeo-resumen animado de la ruta con tus fotos', 'Libro de recuerdos imprimible', 'Comparte la ruta con tarjeta bonita en WhatsApp']}
          >
            <div
              className="aspect-[4/3] rounded-3xl shadow-xl overflow-hidden relative bg-gradient-to-br from-stone-900 via-teal-950 to-stone-900 flex items-center justify-center"
              data-testid="feature-memories-visual"
            >
              {/* Stylised reel card */}
              <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 400 300" preserveAspectRatio="none">
                <path d="M 40 250 C 110 190, 160 210, 210 150 S 330 90, 370 50" stroke="#2dd4bf" strokeWidth="2.5" strokeDasharray="5 7" fill="none" strokeLinecap="round" />
                {[{ x: 40, y: 250 }, { x: 205, y: 152 }, { x: 370, y: 50 }].map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r="6" fill="#f97316" />
                ))}
              </svg>
              {/* Polaroids — fotos reales de viaje */}
              <div className="absolute left-[10%] top-[14%] w-[26%] bg-white p-1.5 pb-4 shadow-2xl rotate-[-8deg] rounded-sm">
                <img src="/landing/atardecer.webp" alt="Atardecer en un viaje" loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
              </div>
              <div className="absolute right-[12%] bottom-[16%] w-[24%] bg-white p-1.5 pb-4 shadow-2xl rotate-[7deg] rounded-sm">
                <img src="/landing/helado.webp" alt="Helado durante un viaje" loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
              </div>
              {/* Play button */}
              <div className="relative w-16 h-16 rounded-full bg-white/15 backdrop-blur border-2 border-white/80 flex items-center justify-center shadow-2xl">
                <Play className="w-7 h-7 text-white fill-white ml-1" />
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-center">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-[11px] font-bold text-stone-900 shadow-lg">
                  <Camera className="w-3.5 h-3.5 text-teal-600" /> Resumen del viaje · 8 paradas · 512 km
                </span>
              </div>
            </div>
          </FeatureRow>

          <FeatureRow
            reverse
            testid="feature-bizum-settle"
            eyebrow="Cierre de cuentas"
            Icon={Mail}
            title="Si queda algo por pagar, Tovías lo cobra por ti"
            description="Al terminar el viaje, cada uno recibe un email con su resumen: qué debe y a quién. Con un toque en «Pagar con Bizum» se salda al momento. Nadie tiene que perseguir a nadie — algo que no encontrarás en otras apps."
            bullets={['Email automático post-viaje con el reparto final', 'Botón «Pagar con Bizum» directo al móvil de tu amigo', 'Deudas saldadas sin conversaciones incómodas']}
          >
            <div
              className="aspect-[4/3] rounded-3xl shadow-xl overflow-hidden relative bg-gradient-to-br from-teal-50 via-emerald-50 to-sky-50 flex items-center justify-center"
              data-testid="feature-bizum-visual"
            >
              {/* Stylised post-trip email */}
              <div className="w-[74%] bg-white rounded-2xl shadow-2xl ring-1 ring-stone-200 overflow-hidden rotate-[-2deg]">
                <div className="bg-stone-900 px-4 py-2.5 flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-teal-400" />
                  <span className="text-[10px] text-white font-semibold truncate">Tu viaje ha terminado — resumen de cuentas</span>
                </div>
                <div className="p-4 space-y-2.5">
                  <div className="text-[11px] text-stone-500">Hola, este fue el reparto final del viaje:</div>
                  <div className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2">
                    <span className="text-[11px] text-stone-700">Debes a <strong>María</strong></span>
                    <span className="text-[12px] font-bold text-stone-900">23,40 €</span>
                  </div>
                  <div className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2">
                    <span className="text-[11px] text-stone-700">Debes a <strong>Álex</strong></span>
                    <span className="text-[12px] font-bold text-stone-900">11,75 €</span>
                  </div>
                  <div className="pt-1 flex justify-center">
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal-600 text-white text-[11px] font-bold shadow-md">
                      Pagar con Bizum <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-3 right-4 bg-white/95 backdrop-blur ring-1 ring-stone-200 rounded-xl px-3 py-1.5 shadow-lg flex items-center gap-1.5 text-[10px] text-stone-700">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Cuentas cerradas en 2 toques
              </div>
            </div>
          </FeatureRow>
        </div>
      </section>

      {/* "Vive viajes así" — recuerdos al FINAL, como cierre emocional de la
          fase "Después". El usuario pidió explícitamente que los recuerdos
          cierren la página, no que la abran. */}
      <PolaroidGallery />

      {/* Todas las funciones — chips de enlace interno hacia /funciones/* */}
      <section className="pb-20 lg:pb-24" data-testid="all-features-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-stone-50 border border-stone-200 p-8 lg:p-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <h2
                className="text-stone-900"
                style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.35rem, 2.5vw, 1.75rem)' }}
              >
                Y esto es solo el principio
              </h2>
              <Link
                to="/funciones"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors"
                data-testid="all-features-link"
              >
                Ver todas las funciones <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {FEATURES.map((f) => (
                <Link
                  key={f.slug}
                  to={`/funciones/${f.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-stone-200 text-sm text-stone-700 font-medium hover:border-teal-400 hover:text-teal-700 transition-colors"
                  data-testid={`feature-chip-${f.slug}`}
                >
                  <f.icon className="w-4 h-4 text-teal-600" />
                  {f.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reseñas reales — ocultas por ahora, se re-activarán cuando
          haya al menos 5 reseñas verificadas post-NPS. El jurado (jul
          2026) señaló que mostrar 1 sola reseña restaba credibilidad
          después del "miles de viajeros" del CTA final. */}
      {/* <ReviewsSection /> */}

      <PricingTeaser />
      <InstallSection />

      {/* Reseñas reales · sep 2026 (Reyes) movidas después del CTA de
          registro — refuerzan la confianza justo cuando el usuario ya
          decidió crear su cuenta, no antes. */}
      <ReviewsCarousel />

      <ActiveEmbajadoresSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}
